import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "@jsq/ds/core";
import { addAction } from "@jsq/observability";
import { PackageDetailDocument, useTriageFindingMutation } from "#graphql/supergraphHooks";
import { FAY_RUM_ACTIONS } from "../constants";
import { hasConflictCode, TRIAGE_CONFLICT_CODES } from "../utils/graphqlConflictCodes";
import type { PackageDetailQuery, TriageStateType } from "#graphql/supergraphTypes";
import type { ChecksTriageAction } from "../viewModelTypes";

export type UseTriageFinding = {
  /** Writes the triage state for a finding. Patches the PackageDetail cache
   *  synchronously (optimistic + on server response) so the row flips without a
   *  refetch. */
  triage: (findingId: string, action: ChecksTriageAction) => Promise<void>;
  /** Whether a triage write is currently in flight for the given finding.
   *  Tracks a set of in-flight ids (not a single one) so concurrent triage on
   *  different rows doesn't clear the spinner prematurely — if row A finishes
   *  while row B is still writing, only A's row un-spins. */
  isPending: (findingId: string) => boolean;
};

// Each triage action maps 1:1 to the envelope's resulting state; the map is
// explicit (not a `${ACTION}D` string trick) so a future action that doesn't
// fit that pattern is caught at compile time and drives both the optimistic
// response and the cache update below.
const RESULTING_STATE_BY_ACTION: Record<ChecksTriageAction, TriageStateType> = {
  CONFIRM_AS_ISSUE: "CONFIRMED_AS_ISSUE",
  IGNORE: "IGNORED",
  UNTRIAGE: "UNTRIAGED",
};

/**
 * Wraps `triageFinding`. One mutation instance serves the whole Checks tab;
 * in-flight tracking is per finding so only that row's controls disable. Skips
 * writing until a valid arena resolves, mirroring the read hooks.
 *
 * Cache patching: `TriageFinding` returns the new `triageState`, so we patch the
 * matching check in the `PackageDetail` query cache both optimistically (instant
 * flip) and again on the real server response — no fire-and-forget refetch, no
 * mid-flight flash of stale buttons after the spinner clears. `refetch` is only
 * called on error, to resync from the server when our optimistic patch is wrong
 * (STALE_FINDING / superseded).
 *
 * @param arenaId Undefined until the host app resolves its arena; the write
 * no-ops until then rather than issuing the required arg as NaN.
 */
export function useTriageFinding(
  refetch: () => void,
  packageId: string | null,
  arenaId: number | undefined,
): UseTriageFinding {
  const [mutate] = useTriageFindingMutation();
  // Track the set of in-flight finding ids so overlapping triage on separate
  // rows doesn't step on each other's spinner state — a single string would
  // let the first finally clear the flag while the second write is still
  // running, mis-enabling the second row's controls.
  const [pendingFindingIds, setPendingFindingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Synchronous in-flight guard. `pendingFindingIds` is a boolean-per-id (a
  // Set), so it can't represent two concurrent writes for the same finding, and
  // the row's control gating is async React state a sub-frame double-click can
  // beat. A ref updates immediately, so we dedupe here: a finding has at most
  // one write in flight, which keeps `pendingFindingIds` accurate (no early
  // re-enable) and prevents two triage actions from racing for the same finding.
  const inFlightFindingIds = useRef<Set<string>>(new Set());

  const triage = useCallback(
    async (findingId: string, action: ChecksTriageAction): Promise<void> => {
      if (arenaId === undefined || packageId === null) {
        return;
      }
      if (inFlightFindingIds.current.has(findingId)) {
        return;
      }
      const nextTriageState = RESULTING_STATE_BY_ACTION[action];
      inFlightFindingIds.current.add(findingId);
      setPendingFindingIds((prev) => {
        const next = new Set(prev);
        next.add(findingId);
        return next;
      });
      try {
        await mutate({
          variables: { input: { findingId, action, arenaId } },
          // Instant flip: Apollo runs `update` with this synthetic response the
          // moment mutate() dispatches, then again with the real response. The
          // real response overwrites the optimistic entry — same triageState,
          // no visible jitter. On error Apollo rolls the optimistic patch back.
          optimisticResponse: {
            triageFinding: {
              __typename: "TriageFindingResult",
              findingId,
              // The mutation returns the DDG-encoded check id string; we don't
              // read it for the cache patch (we key by findingId), so a stub
              // value is safe here and gets replaced by the real one.
              checkId: "",
              triageState: nextTriageState,
            },
          },
          update: (cache, { data }) => {
            const newState = data?.triageFinding?.triageState;
            if (!newState) {
              return;
            }
            cache.updateQuery<PackageDetailQuery>(
              {
                query: PackageDetailDocument,
                variables: { arenaId, id: packageId },
              },
              (existing) => {
                if (!existing?.package) {
                  return existing;
                }
                return {
                  ...existing,
                  package: {
                    ...existing.package,
                    documents: existing.package.documents.map((doc) => ({
                      ...doc,
                      sections: doc.sections.map((section) => ({
                        ...section,
                        checks: section.checks.map((check) =>
                          check.findingId === findingId
                            ? { ...check, triageState: newState }
                            : check,
                        ),
                      })),
                    })),
                  },
                };
              },
            );
          },
        });
        // Numerator of the finding *engagement* rate: triage is the reviewer
        // acting on a finding. `useSubmitFindingFeedback` carries the separate
        // dispute rate — it only ever records a complaint, because
        // FeedbackRating has no positive value. Both divide by the findings
        // count ChecksTab reports.
        //
        // A RUM action rather than a backend metric so both halves of the rate
        // come from the same sampled population. The action rides along so the
        // dashboard can drop `UNTRIAGE`, which undoes a decision rather than
        // making one.
        addAction(FAY_RUM_ACTIONS.findingTriaged, {
          triage_action: action,
          package_id: packageId,
        });
      } catch (error) {
        // A failed write leaves the server as the source of truth; resync from
        // source rather than leaving the optimistic patch in place. Apollo
        // already rolls the optimistic entry back on error, so `refetch` here
        // is belt-and-suspenders. A typed conflict (STALE_FINDING /
        // FINDING_WITHDRAWN) is not a failure of the user's action — the
        // finding moved or its document was removed underneath them — so it
        // gets a neutral "refreshing" message; anything else is a real error.
        if (hasConflictCode(error, TRIAGE_CONFLICT_CODES)) {
          toast.info("This check changed since it loaded — refreshing.");
        } else {
          toast.error("Couldn’t update this check. Please try again.");
        }
        refetch();
      } finally {
        inFlightFindingIds.current.delete(findingId);
        setPendingFindingIds((prev) => {
          const next = new Set(prev);
          next.delete(findingId);
          return next;
        });
      }
    },
    [arenaId, mutate, packageId, refetch],
  );

  const isPending = useCallback(
    (findingId: string): boolean => pendingFindingIds.has(findingId),
    [pendingFindingIds],
  );

  return useMemo(() => ({ triage, isPending }), [triage, isPending]);
}
